import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type WorkOrder, type Evaluation, type Memo } from '../lib/api'
import { money } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'

/**
 * 審核待處理：列出所有「待審核」的發包單，管理員在這裡核准或退回。
 *
 * 刻意不加月份篩選——待審核是暫時狀態，總量本來就少，
 * 而且卡在審核的單不該因為月份選錯就看不到。
 */
export default function Reviews() {
  const [rows, setRows] = useState<WorkOrder[] | null>(null)
  const [evals, setEvals] = useState<Evaluation[] | null>(null)
  const [memos, setMemos] = useState<Memo[] | null>(null)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  /** 正在處理的那一列，避免連點送出兩次。評估與發包單的 id 會撞號，故加前綴 */
  const [busy, setBusy] = useState<string | null>(null)

  const load = () => {
    setRows(null); setEvals(null); setMemos(null)
    api.get<WorkOrder[]>('/work-orders?status=待審核').then(setRows).catch((e) => setError(e.message))
    api.get<Evaluation[]>('/evaluations?status=待審核').then(setEvals).catch((e) => setError(e.message))
    api.get<Memo[]>('/memos?status=待審核').then(setMemos).catch((e) => setError(e.message))
  }

  useEffect(load, [])

  /** 評估與發包單的核准／退回長得一樣，只有端點與顯示文字不同 */
  const reviewEval = async (r: Evaluation, action: 'approve' | 'reject') => {
    setError(''); setMsg('')
    let note = ''
    if (action === 'reject') {
      const input = prompt(`退回「${r.client_name}」這筆評估的原因（會顯示給負責人）`)
      if (input === null) return
      note = input.trim()
      if (note.length < 2) { setError('退回原因至少需要 2 個字'); return }
    } else if (!confirm(
      `確定核准「${r.client_name}」這筆評估？

` +
      `核准後狀態變成「已核准」，才能轉成發包單。
共 ${r.qty} 篇 / ${money(r.amount)} 元。`
    )) return

    setBusy(`e${r.id}`)
    try {
      const res = await api.post<Evaluation>(`/evaluations/${r.id}/review`, { action, note })
      setMsg(action === 'approve'
        ? `已核准「${res.client_name}」，狀態為「${res.status}」`
        : `已退回「${res.client_name}」，負責人會看到退回原因`)
      load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(null)
    }
  }

  const reviewMemo = async (m: Memo, action: 'approve' | 'reject') => {
    setError(''); setMsg('')
    let note = ''
    if (action === 'reject') {
      const input = prompt(`退回「${m.writer_name}」備忘錄的原因（會顯示給建立者）`)
      if (input === null) return
      note = input.trim()
      if (note.length < 2) { setError('退回原因至少需要 2 個字'); return }
    } else if (!confirm(
      `確定核准「${m.writer_name}」的合作備忘錄？

` +
      `合約期限 ${m.effective_from ?? '—'} ～ ${m.effective_to ?? '—'}。
` +
      `核准後立即生效，同一位寫手先前生效中的備忘錄會自動終止。`
    )) return

    setBusy(`m${m.id}`)
    try {
      const res = await api.post<Memo>(`/memos/${m.id}/review`, { action, note })
      setMsg(action === 'approve'
        ? `已核准「${res.writer_name}」的備忘錄，狀態為「${res.status}」`
        : `已退回「${res.writer_name}」的備忘錄`)
      load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(null)
    }
  }

  const review = async (w: WorkOrder, action: 'approve' | 'reject') => {
    setError(''); setMsg('')

    let note = ''
    if (action === 'reject') {
      const input = prompt(`退回 ${w.code} 的原因（會顯示給負責人）`)
      if (input === null) return
      note = input.trim()
      if (note.length < 2) { setError('退回原因至少需要 2 個字'); return }
    } else {
      const ok = confirm(
        `確定核准發包單 ${w.code}？\n\n` +
        `狀態會變成「待確認」，接著就是發給承包人 ${w.writer_name ?? ''} 確認接單。\n` +
        `共 ${w.total_qty} 篇 / ${money(w.total_amount)} 元。`
      )
      if (!ok) return
    }

    setBusy(`w${w.id}`)
    try {
      const res = await api.post<WorkOrder>(`/work-orders/${w.id}/review`, { action, note })
      setMsg(
        action === 'approve'
          ? `已核准 ${res.code}，狀態為「${res.status}」`
          : `已退回 ${res.code}，負責人會看到退回原因`
      )
      load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <div className="page-head">
        <h2>審核待處理</h2>
        {rows && evals && memos && (
          <span className="hint">
            發包單 {rows.length} 張 · 評估 {evals.length} 筆 · 備忘錄 {memos.length} 份待審核
          </span>
        )}
      </div>

      <Alert kind="error">{error}</Alert>
      {msg && <div className="alert ok">{msg}</div>}

      <Panel title={`待審核發包單（${rows?.length ?? 0} 張）`}>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>目前沒有待審核的發包單</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>發包單號</th>
                  <th style={{ width: 56 }}>類型</th>
                  <th>承包人</th>
                  <th>負責人</th>
                  <th>約定交件日</th>
                  <th className="num">篇數</th>
                  <th className="num">金額</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id}>
                    <td><Link to={`/work-orders/${w.id}`}>{w.code}</Link></td>
                    <td>{w.dispatch_type}</td>
                    <td>{w.writer_name}</td>
                    <td>{w.owner_name ?? '—'}</td>
                    <td>{w.due_on ?? '—'}</td>
                    <td className="num">{w.total_qty}</td>
                    <td className="num">{money(w.total_amount)}</td>
                    <td><Tag value={w.status} /></td>
                    <td>
                      <span className="link-actions">
                        <Link to={`/work-orders/${w.id}`}>檢視</Link>
                        <button
                          className="primary"
                          disabled={busy === `w${w.id}`}
                          onClick={() => review(w, 'approve')}
                        >
                          {busy === `w${w.id}` ? '處理中…' : '核准'}
                        </button>
                        <button disabled={busy === `w${w.id}`} onClick={() => review(w, 'reject')}>
                          退回
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>合計 {rows.length} 張</td>
                  <td className="num">{rows.reduce((n, w) => n + w.total_qty, 0)}</td>
                  <td className="num">{money(rows.reduce((n, w) => n + w.total_amount, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          核准後進入「待確認」，等承包人回覆接單；退回會回到「待補充」並附上原因，負責人改完可再送審。
        </p>
      </Panel>

      <Panel title={`待審核評估（${evals?.length ?? 0} 筆）`}>
        {!evals ? (
          <Loading />
        ) : evals.length === 0 ? (
          <Empty>目前沒有待審核的評估</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 74 }}>月份</th>
                  <th>客戶</th>
                  <th style={{ width: 56 }}>類型</th>
                  <th>負責人</th>
                  <th>寫手</th>
                  <th>外包交稿日</th>
                  <th className="num">篇數</th>
                  <th className="num">金額</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {evals.map((r) => (
                  <tr key={r.id}>
                    <td>{r.period}</td>
                    <td>{r.client_name}</td>
                    <td>{r.dispatch_type}</td>
                    <td>{r.owner_name ?? '—'}</td>
                    <td>{r.writer_name ?? '—'}</td>
                    <td>{r.due_on ?? '—'}</td>
                    <td className="num">{r.qty}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td><Tag value={r.status} /></td>
                    <td>
                      <span className="link-actions">
                        <Link to="/evaluations">檢視</Link>
                        <button
                          className="primary"
                          disabled={busy === `e${r.id}`}
                          onClick={() => reviewEval(r, 'approve')}
                        >
                          {busy === `e${r.id}` ? '處理中…' : '核准'}
                        </button>
                        <button disabled={busy === `e${r.id}`} onClick={() => reviewEval(r, 'reject')}>
                          退回
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6}>合計 {evals.length} 筆</td>
                  <td className="num">{evals.reduce((n, r) => n + r.qty, 0)}</td>
                  <td className="num">{money(evals.reduce((n, r) => n + r.amount, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          核准後狀態變成「已核准」，才能在評估表勾選轉成發包單；退回會回到「評估中」並附上原因。
        </p>
      </Panel>

      <Panel title={`待審核合作備忘錄（${memos?.length ?? 0} 份）`}>
        {!memos ? (
          <Loading />
        ) : memos.length === 0 ? (
          <Empty>目前沒有待審核的備忘錄</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>寫手</th>
                  <th>乙方名稱</th>
                  <th>簽約日</th>
                  <th>合約期限</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {memos.map((m) => (
                  <tr key={m.id}>
                    <td><Link to={`/memos/${m.id}`}>{m.writer_name}</Link></td>
                    <td>{m.party_b_title ?? m.writer_name}</td>
                    <td>{m.signed_on ?? '—'}</td>
                    <td>{m.effective_from ?? '—'} ～ {m.effective_to ?? '—'}</td>
                    <td><Tag value={m.status} /></td>
                    <td>
                      <span className="link-actions">
                        <Link to={`/memos/${m.id}/print`}>檢視</Link>
                        <button
                          className="primary"
                          disabled={busy === `m${m.id}`}
                          onClick={() => reviewMemo(m, 'approve')}
                        >
                          {busy === `m${m.id}` ? '處理中…' : '核准'}
                        </button>
                        <button disabled={busy === `m${m.id}`} onClick={() => reviewMemo(m, 'reject')}>
                          退回
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
          核准後立即生效並納入 90 天到期提醒；同一位寫手先前生效中的備忘錄會自動終止。
        </p>
      </Panel>
    </>
  )
}
